import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AGENTS } from "@/lib/agents";
import {
  Send, Wand2, Mail, Plus, TrendingUp, TrendingDown, Wallet, AlertTriangle, Check, Sparkles,
} from "lucide-react";

const SCENES = [
  { id: "casey", ms: 7000, caption: "Casey — Chat through the hard calls" },
  { id: "mica",  ms: 8000, caption: "Mica — Drafts posts + generates visuals" },
  { id: "rex",   ms: 7000, caption: "Rex — Adds leads + drafts outreach" },
  { id: "ada",   ms: 8000, caption: "Ada — Tracks money, calmly" },
];
const TOTAL_MS = SCENES.reduce((s, x) => s + x.ms, 0);

export default function ProductReel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0); // 0..1 within total loop

  // scene switcher
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % SCENES.length), SCENES[idx].ms);
    return () => clearTimeout(t);
  }, [idx, paused]);

  // progress ticker
  useEffect(() => {
    if (paused) return;
    const step = 60; // ms
    const start = Date.now();
    const t = setInterval(() => {
      const elapsedIntoScene = Date.now() - start;
      const priorMs = SCENES.slice(0, idx).reduce((s, x) => s + x.ms, 0);
      setTick(Math.min(1, (priorMs + elapsedIntoScene) / TOTAL_MS));
    }, step);
    return () => clearInterval(t);
  }, [idx, paused]);

  const scene = SCENES[idx];

  return (
    <section id="reel" className="relative z-10 max-w-6xl mx-auto px-8 mb-20">
      <motion.h2
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
        className="font-display text-4xl sm:text-5xl font-bold mb-2"
      >
        <span className="chrome-text">30 seconds inside.</span>
      </motion.h2>
      <p className="text-white/70 mb-8">A silent reel through every workspace. Hover to pause.</p>

      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        data-testid="product-reel"
      >
        {/* Laptop lid */}
        <div className="relative mx-auto max-w-4xl rounded-t-3xl bg-gradient-to-b from-slate-800 to-slate-900 border border-white/15 p-1.5 shadow-2xl">
          {/* Camera notch */}
          <div className="flex justify-center mb-1">
            <div className="w-16 h-1.5 rounded-b-lg bg-black/60 flex items-center justify-center">
              <div className="w-1 h-1 rounded-full bg-white/40" />
            </div>
          </div>

          {/* Screen frame */}
          <div className="relative rounded-2xl overflow-hidden bg-[#0F0524] aspect-[16/9]">
            {/* Browser chrome */}
            <div className="absolute top-0 left-0 right-0 z-30 h-8 bg-slate-900/95 backdrop-blur border-b border-white/10 flex items-center px-3 gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              <div className="ml-3 flex-1 max-w-md h-5 rounded-md bg-white/10 flex items-center px-2">
                <span className="font-mono text-[10px] text-white/50 truncate">solocrew.app / {scene.id}</span>
              </div>
              <Sparkles className="w-3.5 h-3.5 text-neon-pink" />
            </div>

            {/* Scene stage */}
            <div className="absolute inset-0 pt-10">
              <AnimatePresence mode="wait">
                {scene.id === "casey" && <CaseyScene key="casey" />}
                {scene.id === "mica"  && <MicaScene  key="mica"  />}
                {scene.id === "rex"   && <RexScene   key="rex"   />}
                {scene.id === "ada"   && <AdaScene   key="ada"   />}
              </AnimatePresence>
            </div>

            {/* Caption strip */}
            <AnimatePresence mode="wait">
              <motion.div
                key={scene.id + "-cap"}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="absolute bottom-3 left-3 right-3 z-30 flex items-center gap-2"
              >
                <div className="glass rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-white/85 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-neon-pink animate-pulse" />
                  {scene.caption}
                </div>
                {paused && (
                  <span className="glass rounded-full px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-neon-cyan">
                    ⏸ paused
                  </span>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Laptop base */}
        <div className="mx-auto max-w-4xl h-3 bg-gradient-to-b from-slate-700 to-slate-900 rounded-b-md" />
        <div className="mx-auto max-w-[860px] h-2 bg-gradient-to-b from-slate-800 to-slate-950 rounded-b-[2rem]" />

        {/* Scene chips + progress */}
        <div className="mt-6 max-w-4xl mx-auto grid grid-cols-4 gap-2" data-testid="reel-progress">
          {SCENES.map((s, i) => {
            const prior = SCENES.slice(0, i).reduce((sum, x) => sum + x.ms, 0);
            const start = prior / TOTAL_MS;
            const end = (prior + s.ms) / TOTAL_MS;
            const local = tick <= start ? 0 : tick >= end ? 1 : (tick - start) / (end - start);
            const a = AGENTS[s.id === "casey" ? "ceo" : s.id === "mica" ? "marketer" : s.id === "rex" ? "sales" : "accountant"];
            const isActive = i === idx;
            return (
              <button
                key={s.id}
                data-testid={`reel-chip-${s.id}`}
                onClick={() => setIdx(i)}
                className="text-left"
              >
                <div className={"font-mono text-[10px] uppercase tracking-widest mb-1 truncate " + (isActive ? "" : "text-white/40")} style={isActive ? { color: a.color } : undefined}>
                  0{i + 1} · {a.name}
                </div>
                <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full transition-[width] duration-[60ms] ease-linear" style={{ width: `${local * 100}%`, background: a.gradient }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- Scenes ----------

const sceneWrap = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.4 } },
  exit:    { opacity: 0, transition: { duration: 0.25 } },
};

function CaseyScene() {
  const a = AGENTS.ceo;
  const msgs = useMemo(() => ([
    { who: "user",   text: "should we launch monday or wait a week?", at: 300 },
    { who: "casey",  text: "monday. one week of noise > one week of polish.", at: 1400 },
    { who: "user",   text: "ok. what do you need from me?", at: 2400 },
    { who: "casey",  text: "10 min of your voice on the pitch. i'll write the rest.", at: 3400 },
    { who: "system", text: "✓ Mica queued 3 launch posts · Rex booked 2 pilots", at: 5000 },
  ]), []);
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    const timers = msgs.map((m, i) => setTimeout(() => setVisible(i + 1), m.at));
    return () => timers.forEach(clearTimeout);
  }, [msgs]);

  return (
    <motion.div {...sceneWrap} className="absolute inset-0 p-6 flex flex-col aurora">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 slime-blob overflow-hidden" style={{ background: a.gradient }}>
          <img src={a.avatar} alt="" className="w-full h-full object-cover mix-blend-luminosity" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-neon-chrome">Casey · CEO</div>
          <div className="font-display text-lg font-bold chrome-text">Co-founder chat</div>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-hidden">
        <AnimatePresence>
          {msgs.slice(0, visible).map((m) => (
            <motion.div
              key={m.at + "-" + m.who}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className={m.who === "user" ? "flex justify-end" : m.who === "system" ? "flex justify-center" : "flex justify-start"}
            >
              <div className={
                "max-w-[70%] rounded-2xl px-3 py-1.5 text-xs " +
                (m.who === "user"
                  ? "bg-neon-pink text-white rounded-br-sm font-medium"
                  : m.who === "system"
                    ? "bg-neon-lime/20 text-neon-lime border border-neon-lime/40 font-mono uppercase tracking-widest text-[10px]"
                    : "bg-white/10 text-white border border-white/15 rounded-bl-sm")
              }>
                {m.text}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function MicaScene() {
  const a = AGENTS.marketer;
  const full = "Loopcup launches Monday in Berlin ☕ your customers keep asking for less waste. free 30-day pilot — reply LOOP.";
  const [typed, setTyped] = useState("");
  const [step, setStep] = useState(0); // 0 typing, 1 generating, 2 done

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i += 2;
      if (i >= full.length) { setTyped(full); clearInterval(t); setStep(1); setTimeout(() => setStep(2), 1800); }
      else setTyped(full.slice(0, i));
    }, 45);
    return () => clearInterval(t);
  }, []);

  return (
    <motion.div {...sceneWrap} className="absolute inset-0 p-6 aurora">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 slime-blob overflow-hidden" style={{ background: a.gradient }}>
          <img src={a.avatar} alt="" className="w-full h-full object-cover mix-blend-luminosity" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: a.color }}>Mica · Marketer</div>
          <div className="font-display text-lg font-bold" style={{ color: a.color }}>Draft a post</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 h-[calc(100%-3rem)]">
        <div className="glass rounded-xl p-3 flex flex-col">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">Topic</div>
          <div className="text-white text-sm mb-3">Launch Loopcup pilot in Berlin</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">Post</div>
          <div className="flex-1 bg-slate-950/60 border border-white/10 rounded-lg p-2 font-mono text-[11px] text-neon-lime/90 whitespace-pre-wrap">
            {typed}<span className="inline-block w-1 h-3 bg-neon-lime/90 align-middle ml-0.5 animate-pulse" />
          </div>
          <motion.div
            animate={step >= 1 ? { scale: [1, 1.05, 1] } : {}} transition={{ duration: 0.5 }}
            className="mt-3 rounded-full bg-neon-pink text-white font-bold text-xs px-3 py-2 flex items-center justify-center gap-1 neon-glow-pink"
          >
            <Wand2 className="w-3 h-3" /> {step === 0 ? "typing…" : step === 1 ? "generating visual…" : "queued for approval ✓"}
          </motion.div>
        </div>

        <div className="glass rounded-xl p-3 flex flex-col">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1">Visual</div>
          <div className="flex-1 rounded-lg overflow-hidden relative border border-white/10">
            {step < 2 ? (
              <motion.div className="absolute inset-0 flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FF007F 0%,#FF9900 100%)" }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}>
                  <Sparkles className="w-8 h-8 text-white" />
                </motion.div>
                <div className="absolute inset-0" style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0 6px, transparent 6px 12px)" }} />
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: "linear-gradient(135deg,#FF007F 0%,#FF9900 60%,#B026FF 100%)" }}
              >
                <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.3), transparent 40%), radial-gradient(circle at 70% 80%, rgba(0,255,255,0.3), transparent 40%)" }} />
                <div className="relative text-center px-3">
                  <div className="font-display text-white text-2xl font-bold drop-shadow-lg">LOOPCUP</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/90">Berlin · Monday</div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function RexScene() {
  const a = AGENTS.sales;
  const leads = [
    { name: "Café Kranzler",  stage: "new",       delay: 0 },
    { name: "The Barn Roaster", stage: "contacted", delay: 900 },
    { name: "Bonanza Coffee", stage: "engaged",   delay: 1800 },
  ];
  const [showEmail, setShowEmail] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShowEmail(true), 3200); return () => clearTimeout(t); }, []);

  return (
    <motion.div {...sceneWrap} className="absolute inset-0 p-6 aurora">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 slime-blob overflow-hidden" style={{ background: a.gradient }}>
          <img src={a.avatar} alt="" className="w-full h-full object-cover mix-blend-luminosity" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: a.color }}>Rex · Sales Rep</div>
          <div className="font-display text-lg font-bold" style={{ color: a.color }}>Pipeline</div>
        </div>
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2.6, type: "spring" }} className="ml-auto rounded-full bg-neon-cyan text-slate-900 font-bold text-xs px-3 py-1 flex items-center gap-1 neon-glow-cyan">
          <Plus className="w-3 h-3" /> Lead added
        </motion.div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        {leads.map((l, i) => (
          <motion.div
            key={l.name}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: l.delay / 1000 }}
            className="glass rounded-xl p-3"
          >
            <div className="font-display text-sm font-bold text-white">{l.name}</div>
            <div className="mt-2 inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/40">
              {l.stage}
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showEmail && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="glass rounded-xl p-3 border border-neon-cyan/30"
          >
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-3.5 h-3.5 text-neon-cyan" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-neon-cyan">Draft to Café Kranzler</div>
            </div>
            <div className="font-mono text-[11px] text-neon-lime/90 leading-tight">
              Hi — I run Loopcup, a reusable cup pilot in Berlin. 30 days free, zero setup. 15 min next week to walk through it?
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function AdaScene() {
  const [flag, setFlag] = useState(false);
  useEffect(() => { const t = setTimeout(() => setFlag(true), 4200); return () => clearTimeout(t); }, []);

  return (
    <motion.div {...sceneWrap} className="absolute inset-0 p-6" style={{ background: "#F8FAFC", color: "#0F172A" }}>
      <div className="flex items-center gap-3 mb-4">
        <img src={AGENTS.accountant.avatar} alt="" className="w-10 h-10 rounded-xl object-cover bg-indigo-50" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-indigo-500">Ada · Accountant</div>
          <div className="font-display text-lg font-bold text-slate-900">Calm money view</div>
        </div>
        <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-400">calm mode ✓</div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-3">
        <Kpi icon={TrendingUp}   label="Income"   value="$4,820"  end={4820}  color="text-emerald-600 bg-emerald-50" />
        <Kpi icon={TrendingDown} label="Expenses" value="$1,940"  end={1940}  color="text-slate-600 bg-slate-100" />
        <Kpi icon={Wallet}       label="Net"      value="$2,880"  end={2880}  color="text-emerald-600 bg-emerald-50" />
        <Kpi icon={AlertTriangle}label="Runway"   value="2.4 mo"  end={2.4}   color="text-red-600 bg-red-50" suffix=" mo" decimals={1} />
      </div>

      <div className="grid grid-cols-2 gap-3 h-[calc(100%-8rem)]">
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="font-display text-sm font-bold text-slate-900 mb-2">Latest</div>
          {[
            { d: "Stripe payout",  a: "+$1,200", pos: true, cat: "Income" },
            { d: "Figma",          a: "−$15",    pos: false, cat: "Tools" },
            { d: "Notion",         a: "−$10",    pos: false, cat: "Tools" },
            { d: "Client — Loop",  a: "+$800",   pos: true, cat: "Income" },
          ].map((t, i) => (
            <motion.div
              key={t.d}
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.25 }}
              className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.pos ? "#10b981" : "#94a3b8" }} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-800 truncate">{t.d}</div>
                <div className="text-[10px] text-slate-500">{t.cat}</div>
              </div>
              <div className={"font-mono text-xs font-semibold " + (t.pos ? "text-emerald-600" : "text-slate-700")}>{t.a}</div>
            </motion.div>
          ))}
        </div>

        <AnimatePresence>
          {flag && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="bg-white border-l-4 border-l-red-500 border border-slate-200 rounded-xl p-3"
            >
              <div className="flex items-start gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                <div className="font-display text-sm font-bold text-slate-900">Runway below 3 months</div>
              </div>
              <p className="text-xs text-slate-600">I can prep a variance summary. Or draft a light expense trim before month-end.</p>
              <div className="mt-2 flex items-center gap-1 text-[10px] font-mono text-emerald-600">
                <Check className="w-3 h-3" /> flagged in your standup
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className={"w-7 h-7 rounded-lg flex items-center justify-center mb-1 " + color}><Icon className="w-3.5 h-3.5" /></div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="text-xl font-bold text-slate-900" style={{ fontFamily: "Fredoka" }}
      >
        {value}
      </motion.div>
    </div>
  );
}
